import { Injectable } from '@angular/core';
import { UserService } from '@modules/auth/services/user.service';
import { combineLatest, distinctUntilChanged, filter, map, merge, Observable, race, startWith, take, timer } from 'rxjs';

import { PlansBundle } from '../models/plans.model';
import { PlansService } from './plans.service';

/**
 * Planes con acceso a Nina: todos los pagos (orden comercial en `form-pay-plan.component`:
 * economico 1, estandar 2, fiscal 3). Solo `gratis` queda fuera. Debe coincidir con
 * `NINA_ALLOWED_PLAN_TYPES` de la API (`utils/assistant-access.util.ts`).
 *
 * OJO: esta lista es un literal **ya normalizado** que se compara con `.includes()` contra la
 * entrada normalizada, así que va `'economico'` SIN tilde. La constante equivalente de la API es
 * `'económico'` CON tilde, pero allá la lista pasa por `.map(normalizePlanType)`. Escribir la
 * tilde acá compila, pasa el lint y no hace match nunca.
 */
const NINA_ALLOWED_PLAN_TYPES = ['economico', 'estandar', 'fiscal'];

/**
 * Tope de espera del gate asíncrono. `plansBundle$` arranca en null y se llena tras el login, así
 * que hay que esperarlo; si no llega (p. ej. falló `GET /plans`), se deniega en vez de colgar la
 * navegación.
 */
const GATE_WAIT_MS = 5000;

/**
 * Reglas de acceso al módulo del asistente (Nina). Fuente única de la condición, consumida por:
 *   - `AssistantAccessGuard` (ruta `/asistente`)
 *   - el botón "Preguntar a Nina" del side-nav
 *   - `AssistantComponent` (para no disparar consultas fuera del feature)
 *
 * Vive en `app-common` porque el side-nav también la necesita y no puede importar del módulo lazy
 * del asistente.
 *
 * Las dos condiciones, ambas fail-closed:
 *   1. `user.isOwner` — dueño del comercio. Lo calcula la API y viaja en el response de
 *      `/auth/signin` y `/auth/verified-user`. No hay sistema de roles que consultar.
 *   2. Plan pago: económico, estándar o fiscal (solo `gratis` queda fuera).
 *
 * La API impone las dos por su cuenta con `requireAssistantAccess` y responde 403. Ya no hay
 * flag de PostHog (`nina_flag` se retiró): una caída de /flags no puede ocultar a Nina.
 */
@Injectable({ providedIn: 'root' })
export class AssistantAccessService {
    constructor(private userService: UserService, private plansService: PlansService) {}

    /**
     * Minúsculas y sin diacríticos, para comparar `Plan.type` (que es un `String` libre, sin enum,
     * y llega con tilde o sin ella según el plan). Mismo criterio que el `normalizePlanType`
     * privado de `form-pay-plan.component.ts` y que el de la API.
     */
    normalizePlanType(type: string | null | undefined): string {
        return (type ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
    }

    /** Lectura síncrona: para los `*ngIf` y las defensas de handler. */
    canAccessSync(): boolean {
        return this.evaluate(this.userService.user$.value, this.plansService.plansBundle$.value);
    }

    /**
     * Versión asíncrona para el guard de ruta: espera a que existan usuario y bundle de planes
     * antes de decidir. Sin esta espera, un F5 sobre `/asistente` leería `plansBundle$` en null y
     * expulsaría al dueño.
     */
    canAccess$(): Observable<boolean> {
        const user$ = this.userService.user$.pipe(
            filter(user => !!user),
            take(1),
        );
        const bundle$ = this.plansService.plansBundle$.pipe(
            filter(bundle => !!bundle),
            take(1),
        );

        return race(
            combineLatest([user$, bundle$]).pipe(
                take(1),
                map(([user, bundle]) => this.evaluate(user, bundle)),
            ),
            // Si usuario o planes no llegan a tiempo, se deniega (fail-closed).
            timer(GATE_WAIT_MS).pipe(map(() => false)),
        );
    }

    /**
     * Emite el estado del gate cada vez que cambia cualquiera de sus dos entradas. Las dos son
     * asíncronas y con tiempos distintos: el usuario y el bundle de planes llegan tras el login.
     *
     * Arranca emitiendo el valor actual, así el consumidor no necesita una lectura inicial aparte.
     */
    canAccessChanges$(): Observable<boolean> {
        return merge(this.userService.user$, this.plansService.plansBundle$).pipe(
            map(() => this.canAccessSync()),
            startWith(this.canAccessSync()),
            distinctUntilChanged(),
        );
    }

    private evaluate(user: any, bundle: PlansBundle | null): boolean {
        return !!user?.isOwner && this.planAllows(bundle);
    }

    private planAllows(bundle: PlansBundle | null): boolean {
        return NINA_ALLOWED_PLAN_TYPES.includes(this.normalizePlanType(bundle?.currentPlan?.type));
    }
}
